import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOCATION_NAMES, ROLE_LABELS } from "@/lib/format";
import { AlertsBell } from "@/components/AlertsBell";
import { ShoppingCart, Globe, Package, Receipt, Users as UsersIcon, BarChart3, ShieldCheck, Sliders, LogOut, Sun, Moon, Coffee, Store } from "lucide-react";

const NAV = [
  { name: "Ventas", icon: ShoppingCart, route: "/pos", mod: "ventas" },
  { name: "Pedidos Online", icon: Globe, route: "/pedidos-online", mod: "pedidos" },
  { name: "Inventario", icon: Package, route: "/inventario", mod: "inventario" },
  { name: "Compras & Gastos", icon: Receipt, route: "/compras", mod: "compras" },
  { name: "Usuarios", icon: UsersIcon, route: "/usuarios", mod: "usuarios" },
  { name: "Reportes", icon: BarChart3, route: "/reportes", mod: "reportes" },
  { name: "Auditoría", icon: ShieldCheck, route: "/auditoria", mod: "auditoria" },
  { name: "Administración", icon: Sliders, route: "/administracion", mod: "administracion" },
];

export default function Layout({ children }) {
  const { user, can, logout, location, setLocation, theme, toggleTheme } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter((n) => can(n.mod));

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-border bg-card/95 backdrop-blur-md flex flex-col fixed h-screen z-40" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center neon-glow">
            <Coffee className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <p className="font-head font-extrabold text-lg leading-none tracking-tight">Nauta<span className="text-primary">POS</span></p>
            <p className="text-[10px] text-muted-foreground">Sistema de Gestión</p>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((it) => (
            <NavLink key={it.route} to={it.route} data-testid={`nav-${it.mod}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-primary text-primary-foreground neon-glow" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}>
              <it.icon className="w-[18px] h-[18px]" />
              {it.name}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors w-full">
            <LogOut className="w-[18px] h-[18px]" /> Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        <header className="h-16 border-b border-border bg-card/70 backdrop-blur-md flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 bg-secondary rounded-lg p-1" data-testid="location-switcher">
            <Store className="w-4 h-4 text-primary ml-2" />
            {Object.keys(LOCATION_NAMES).map((loc) => (
              <button key={loc} onClick={() => setLocation(loc)} data-testid={`loc-${loc}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  location === loc ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}>
                {LOCATION_NAMES[loc]}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {["super-admin", "administrador"].includes(user?.role) && <AlertsBell />}
            <button onClick={toggleTheme} data-testid="theme-toggle"
              className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-accent transition-colors">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <div className="flex items-center gap-2 pl-3 border-l border-border">
              <div className="text-right">
                <p className="text-sm font-semibold leading-none">{user?.name}</p>
                <p className="text-[10px] text-primary font-medium">{ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
              <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold font-head">
                {user?.name?.[0]?.toUpperCase()}
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-6 animate-fade-up">{children}</main>
      </div>
    </div>
  );
}
